import { assignIn, pick } from 'lodash-es'
import { v4 as guid } from 'uuid'
import { fabric } from 'fabric'
import { privileges, whiteboardConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import appConfig from '@/services/appConfig'
import type MyArticle from '@/models/myArticle'
import type DuneAsset from '@/models/duneAsset'
import utils from '@/services/utils'
import type { ArticleFilterModel } from '@/api/t1/model/articleModel'
import { getArticlesStream } from '@/api/t1/article'
import { FilterCriteriaMode } from '@/models/filterCriteria'

export interface IWbDesignOptionOptions extends fabric.IImageOptions, IWbObjectProps {
  id?: string
  lock?: boolean
  preventUnlock?: boolean
  preventDelete?: boolean
  isUpdatingImage?: boolean
  assetStorageFile: string
  articleId?: number
  article?: MyArticle
  asset?: DuneAsset
  assetId: string

}

export default class WbDesignOption extends fabric.Image implements IWbObject {
  public id: string
  public type = whiteboardConstants.objectTypes.designOption
  public lock: boolean
  public preventUnlock: boolean
  public connectable: boolean
  public localOnly?: boolean | undefined
  public excludeFromGroupSelection?: boolean | undefined
  public preventDelete?: boolean
  public canBeLinkedTo = false
  public selected = false
  public canLink = true
  public link = undefined
  public isUpdatingImage: boolean
  public assetStorageFile: string
  public asset?: DuneAsset
  public articleId: number
  public assetId: string
  public article?: MyArticle
  public editableProps: Record<string, IWbObjectProp> = {
    // scale: { name: 'size', type: 'scale', editable: true },
    // lock: { name: 'Lock', type: 'lock', editable: true, editableWhenLocked: true },
    // info: { name: 'info', type: 'info', editable: true, editableWhenLocked: true },
  }

  public actions: Record<string, IWObjectActions> = {
    assignAsArticleImage: { action: 'assignAsArticleImage', label: 'Assign As Article Image', faicon: 'fa-light fa-check-double', showInSubMenu: (() => {
      const userStore = useUserStore()
      return (userStore.userProfile.isValidPrivilege(privileges.whiteboard.allowExternalImageToArticle) && utils.isValidStringValue(userStore.activeCatalog?.ContextReadWriteKey))
    })() },
    fullScreen: { action: 'fullScreen', label: 'Full Screen', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
  }

  constructor(e: HTMLImageElement, opt?: IWbDesignOptionOptions) {
    super(e, opt)
    this.id = opt?.id || guid()

    this.lock = opt?.lock || false
    this.connectable = true
    this.preventUnlock = opt?.preventUnlock || true
    this.localOnly = utils.isDefined(opt?.localOnly) ? opt.localOnly : true
    this.excludeFromGroupSelection = utils.isDefined(opt?.excludeFromGroupSelection) ? opt.excludeFromGroupSelection : true
    this.preventDelete = utils.isDefined(opt?.preventDelete) ? opt.preventDelete : true
    this.setLock(this.lock)
    this.objectCaching = false
    this.article = opt?.article
    this.articleId = opt?.articleId || -1
    this.assetId = opt?.assetId || ''
    this.asset = opt?.asset
    this.statefullCache = false
    this.noScaleCache = true
    this.needsItsOwnCache = () => false
    this.isUpdatingImage = false
    this.assetStorageFile = opt?.assetStorageFile || ''
    this.stateProperties = this.stateProperties?.concat(['lock'])
    this.on('selected', () => {
      this.selected = true
    })
    this.on('deselected', () => {
      this.selected = false
    })
  }

  setProp(prop: string, value: any, ignoreHistory: boolean = false, notifyObjectModified: boolean = true) {
    switch (prop) {
      case 'lock':
        this.set('lock', value.lock)
        this.setLock(value.lock)
        break
      default:
        console.warn('Attempting to set unsupported WbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    if (notifyObjectModified) {
      this.canvas?.fire('object:modified', { target: this, ignoreHistory })
    }
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'lock':
        result.lock = this.lock
        break
      default:
        console.warn('Attempting to get unsupported WbObjectProp', prop)
    }
    return result
  }

  setLock(lock: boolean) {
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'width',
      'height',
      'angle',
      'scaleX',
      'scaleY',
      'lock',
      'version',
      'localOnly',
      'assetId',
      'articleId',
      'assetStorageFile',
      'preventDelete',
      'excludeFromGroupSelection',
      'preventUnlock',
    ]
    return { ...pick(this, propsToPluck) }
  }

  static fromObject(object: WbDesignOption, callback?: Function) {
    this.loadFromUrl(object, 500, 500).then((v) => { callback && callback(v) })
  }

  static async loadFromUrl(opt: IWbDesignOptionOptions, width: number = 200, height: number = 200) {
    const userStore = useUserStore()
    if (!opt.article && userStore.activeCatalog && appConfig && appConfig.DB && userStore.myAttributes) {
      const filters: ArticleFilterModel[] = []
      if (Object.keys(userStore.linkedCatalogDetails).length > 0) {
        const linkedCatalogCodes: number[] = []
        for (const linkedCatalogCode of Object.keys(userStore.linkedCatalogDetails)) {
          linkedCatalogCodes.push(Number(linkedCatalogCode))
        }
        filters.push({ attribute: '_Seasons', mode: FilterCriteriaMode.multiString, value: linkedCatalogCodes, exclude: false })
      }
      const responseArticles = await getArticlesStream(userStore.activeCatalog.CatalogCode, { articleId: opt.articleId, body: { filters } }, whiteboardConstants.abortController.signal)

      let responseArticle = responseArticles.find(x => x.CatalogCode === userStore.activeCatalog!.CatalogCode && x.Id === opt.articleId)
      if (!responseArticle) {
        responseArticle = responseArticles.find(x => x.Id === opt.articleId)
      }
      if (responseArticle) {
        const myArticles = await appConfig.DB.convertToMyArticles(userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes, userStore.currentUsername, userStore.userProfile.Id, [responseArticle], userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, userStore.currentCustomerSegmentations, true)
        if (myArticles.length) {
          opt.article = myArticles[0]
        }
      }
      else {
        console.log('something is wrong')
      }
    }

    const url = `${appConfig.AssetsUrl}/assets/content/${opt.assetStorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog!.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
    return new Promise((resolve, reject) => {
      fabric.util.loadImage(url, (oImg) => {
        const img = new Image()
        img.onload = () => {
          const options = assignIn({ top: 10 + img.height / 2, left: 10 + img.width / 2 }, opt)
          resolve(new WbDesignOption(oImg, options))
        }
        img.onerror = (err) => {
          console.error('Error loading image', url)
          reject(err)
        }
        img.src = url
      }, null, 'anonymous')
    })
  }

  static getNearestResolution(size, actualSize) {
    const userStore = useUserStore()
    const resolutions = userStore.activeCatalog?.Config.ImageDunesSizesList || [200, 400, 800, 1200, 1600, 2000]
    if (actualSize > resolutions[resolutions.length - 1]) {
      resolutions.push(actualSize)
    }
    const res = resolutions
    let closest = res[0]
    let minDiff = Math.abs(size - closest)

    for (let i = 1; i < res.length; i++) {
      const diff = Math.abs(size - res[i])
      if (diff < minDiff) {
        minDiff = diff
        closest = res[i]
      }
    }
    // If the suggested resolution is higher than the actual image, fallback to actual
    if (closest > actualSize) {
      return actualSize
    }
    return closest
  }

  static getDesignOptionScale(actualWidth, actualHeight, maxSize = 200) {
    let scaleX = 1
    let scaleY = 1

    if (actualWidth > actualHeight) {
      if (actualWidth > maxSize) {
        const newHeight = actualHeight * (maxSize / actualWidth)
        scaleX = maxSize / actualWidth
        scaleY = newHeight / actualHeight
      }
    }
    else {
      if (actualHeight > maxSize) {
        const newWidth = actualWidth * (maxSize / actualHeight)
        scaleX = newWidth / actualWidth
        scaleY = maxSize / actualHeight
      }
    }

    return Math.max(scaleX, scaleY)
  }

  static getDesignOptionHeightWidth(width: number, height: number, scaleFactor: number) {
    const isWidthLarger = width >= height
    const maxSide = Math.max(width, height)
    const scaledMax = maxSide * scaleFactor

    let scaledWidth: number, scaledHeight: number
    if (isWidthLarger) {
      scaledWidth = Number(scaledMax)
      scaledHeight = Number(scaledMax * (height / width))
    }
    else {
      scaledHeight = Number(scaledMax)
      scaledWidth = Number(scaledMax * (width / height))
    }

    return { scaledWidth, scaledHeight }
  }

  override _render(ctx: CanvasRenderingContext2D): void {
    super._render(ctx)
    if (this.selected) {
      const width = this.width || 0
      const height = this.height || 0
      const fontSize = Math.max(10, Math.min(width / 10, 24))
      ctx.font = `${fontSize}px Helvetica`
      ctx.textAlign = 'left'
      const measure = ctx.measureText('Design Option')
      ctx.fillStyle = '#1484f8'

      ctx.fillRect(-width / 2, -height / 2, measure.width, 40)
      ctx.fillStyle = '#FFFFFF'
      ctx.fillText('Design Option', -width / 2, -height / 2 + 25)
    }
  }
}

const f: any = fabric
f.WbDesignOption = WbDesignOption
